import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface ChatRoom {
  id: string;
  type: 'direct' | 'group' | 'support';
  name?: string;
  description?: string;
  avatar_url?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: string;
  room_id: string;
  sender_id: string;
  content: string;
  message_type: 'text' | 'image' | 'file' | 'system';
  attachments?: any[];
  metadata?: Record<string, any>;
  edited: boolean;
  deleted: boolean;
  reply_to?: string;
  created_at: string;
  sender?: {
    id: string;
    full_name?: string;
    avatar_url?: string;
  };
}

export interface ChatParticipant {
  id: string;
  room_id: string;
  user_id: string;
  role: 'admin' | 'moderator' | 'member';
  last_read_at: string;
  joined_at: string;
}

export const useChat = (roomId?: string) => {
  const { user } = useAuth();
  const [rooms, setRooms] = useState<ChatRoom[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [participants] = useState<ChatParticipant[]>([]);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch user's chat rooms
  const fetchRooms = useCallback(async () => {
    if (!user) return;

    try {
      // First, get all rooms where current user is a participant
      const { data: participantData, error: participantError } = await supabase
        .from('chat_participants')
        .select('room_id')
        .eq('user_id', user.id);

      if (participantError) throw participantError;

      if (!participantData || participantData.length === 0) {
        setRooms([]);
        return;
      }

      const roomIds = participantData.map(p => p.room_id);

      // Get room details
      const { data: roomsData, error: roomsError } = await supabase
        .from('chat_rooms')
        .select('*')
        .in('id', roomIds);

      if (roomsError) throw roomsError;

      // Process each room and get participant details
      const roomsWithDetails = await Promise.all(
        (roomsData || []).map(async (room: any) => {
          // For direct chats, get the other participant's info
          if (room.type === 'direct') {
            // Get other participants in this room
            const { data: otherParticipants } = await supabase
              .from('chat_participants')
              .select('user_id')
              .eq('room_id', room.id)
              .neq('user_id', user.id);

            if (otherParticipants && otherParticipants.length > 0) {
              const otherUserId = otherParticipants[0].user_id;

              // Get profile info
              const { data: profileData } = await supabase
                .from('profiles')
                .select('full_name, avatar_url')
                .eq('id', otherUserId)
                .single();

              // Get breeder profile info if available
              const { data: breederData } = await supabase
                .from('breeder_profiles')
                .select('business_name, city')
                .eq('user_id', otherUserId)
                .single();

              // Prefer breeder business name if available, else profile name
              const name = breederData?.business_name ||
                profileData?.full_name ||
                'Unknown User';

              const avatar = profileData?.avatar_url;

              return {
                ...room,
                name,
                avatar_url: avatar,
                other_participant_id: otherUserId
              };
            }
          }
          
          return room;
        })
      );

      const validRooms = roomsWithDetails.filter(Boolean) as ChatRoom[];
      setRooms(validRooms);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  }, [user]);

  // Fetch messages for a room
  const fetchMessages = useCallback(async (chatRoomId: string) => {
    if (!user || !chatRoomId) return;
    
    // Validate chatRoomId is a string and not an object
    if (typeof chatRoomId !== 'string') {
      console.error('Invalid chatRoomId type:', typeof chatRoomId, chatRoomId);
      return;
    }

    // If chatRoomId is an object with an id property, extract it
    let roomId = chatRoomId;
    if (typeof chatRoomId === 'object' && chatRoomId !== null && 'id' in chatRoomId) {
      roomId = (chatRoomId as any).id;
    }

    if (typeof roomId !== 'string') {
      console.error('Could not extract valid room ID:', chatRoomId);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('chat_messages')
        .select(`
          *
        `)
        .eq('room_id', roomId)
        .eq('deleted', false)
        .order('created_at', { ascending: true })
        .limit(100);

      if (error) throw error;

      // Fetch sender information for each message
      const messagesWithSenders = await Promise.all(
        (data || []).map(async (msg: any) => {
          const { data: senderData, error: senderError } = await supabase
            .from('profiles')
            .select('id, full_name, avatar_url')
            .eq('id', msg.sender_id)
            .single();

          // Handle case where profile might not exist
          if (senderError) {
            console.warn('Could not fetch sender profile:', senderError);
          }

          return {
            ...msg,
            sender: {
              id: msg.sender_id,
              full_name: senderData?.full_name || 'Unknown User',
              avatar_url: senderData?.avatar_url || '',
            },
          };
        })
      );

      setMessages(messagesWithSenders as ChatMessage[]);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Send message
  const sendMessage = useCallback(async (
    chatRoomId: string,
    content: string,
    messageType: 'text' | 'image' | 'file' = 'text',
    attachments?: any[]
  ) => {
    if (!user || !content.trim() || !chatRoomId) return;
    
    // Validate and extract room ID
    let roomId = chatRoomId;
    if (typeof chatRoomId === 'object' && chatRoomId !== null && 'id' in chatRoomId) {
      roomId = (chatRoomId as any).id;
    }
    
    if (typeof roomId !== 'string') {
      console.error('Invalid chatRoomId type in sendMessage:', typeof chatRoomId, chatRoomId);
      throw new Error('Invalid room ID');
    }

    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          room_id: roomId,
          sender_id: user.id,
          content: content.trim(),
          message_type: messageType,
          attachments: attachments || [],
        } as any)
        .select()
        .single();

      if (error) throw error;

      // Update last_read_at for sender
      await supabase
        .from('chat_participants')
        // @ts-ignore - Supabase type generation issue
        .update({ last_read_at: new Date().toISOString() })
        .eq('room_id', roomId)
        .eq('user_id', user.id);

      return data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }, [user]);

  // Create or get direct chat room
  const createDirectChat = useCallback(async (otherUserId: string) => {
    if (!user) return null;

    try {
      // @ts-ignore - Supabase RPC type generation issue
      const { data, error } = await supabase.rpc('get_or_create_direct_chat', {
        p_user1_id: user.id,
        p_user2_id: otherUserId,
      });

      if (error) throw error;

      await fetchRooms();
      
      // The function returns {id: "room_id"}, so extract the ID
      return data?.id || data;
    } catch (error) {
      console.error('Error creating direct chat:', error);
      return null;
    }
  }, [user, fetchRooms]);

  // Mark messages as read
  const markAsRead = useCallback(async (chatRoomId: string) => {
    if (!user) return;

    // Validate and extract room ID
    let roomId = chatRoomId;
    if (typeof chatRoomId === 'object' && chatRoomId !== null && 'id' in chatRoomId) {
      roomId = (chatRoomId as any).id;
    }
    
    if (typeof roomId !== 'string') {
      console.error('Invalid room ID in markAsRead:', chatRoomId);
      return;
    }

    try {
      await supabase
        .from('chat_participants')
        // @ts-ignore - Supabase type generation issue
        .update({ last_read_at: new Date().toISOString() })
        .eq('room_id', roomId)
        .eq('user_id', user.id);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  }, [user]);

  // Set typing indicator
  const setTyping = useCallback(async (chatRoomId: string, isTyping: boolean) => {
    if (!user) return;

    // Validate and extract room ID
    let roomId = chatRoomId;
    if (typeof chatRoomId === 'object' && chatRoomId !== null && 'id' in chatRoomId) {
      roomId = (chatRoomId as any).id;
    }
    
    if (typeof roomId !== 'string') {
      console.error('Invalid room ID in setTyping:', chatRoomId);
      return;
    }

    try {
      if (isTyping) {
        await supabase
          .from('chat_typing_indicators')
          .upsert({
            room_id: roomId,
            user_id: user.id,
            expires_at: new Date(Date.now() + 10000).toISOString(),
          } as any);
      } else {
        await supabase
          .from('chat_typing_indicators')
          .delete()
          .eq('room_id', roomId)
          .eq('user_id', user.id);
      }
    } catch (error) {
      console.error('Error setting typing indicator:', error);
    }
  }, [user]);

  // Subscribe to real-time messages
  useEffect(() => {
    if (!roomId || !user) return;

    fetchMessages(roomId);

    const messagesChannel = supabase
      .channel(`room:${roomId}:messages`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `room_id=eq.${roomId}`,
        },
        async (payload) => {
          const newMessage = payload.new as ChatMessage;

          // Fetch sender info
          const { data: senderData, error: senderError } = await supabase
            .from('profiles')
            .select('full_name, avatar_url')
            .eq('id', newMessage.sender_id)
            .single();

          if (senderError) {
            console.warn('Could not fetch sender profile for real-time message:', senderError);
          }

          const sender = senderData || { full_name: 'Unknown User', avatar_url: '' };

          setMessages(prev => [...prev, {
            ...newMessage,
            sender: {
              id: newMessage.sender_id,
              full_name: sender.full_name || 'Unknown User',
              avatar_url: sender.avatar_url || '',
            },
          }]);
        }
      )
      .subscribe();

    // Subscribe to typing indicators
    const typingChannel = supabase
      .channel(`room:${roomId}:typing`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_typing_indicators',
          filter: `room_id=eq.${roomId}`,
        },
        async (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const indicator = payload.new as any;
            if (indicator.user_id !== user.id) {
              setTypingUsers(prev =>
                prev.includes(indicator.user_id) ? prev : [...prev, indicator.user_id]
              );
            }
          } else if (payload.eventType === 'DELETE') {
            const indicator = payload.old as any;
            setTypingUsers(prev => prev.filter(id => id !== indicator.user_id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(typingChannel);
    };
  }, [roomId, user, fetchMessages]);

  // Fetch rooms on mount
  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  return {
    rooms,
    messages,
    participants,
    typingUsers,
    loading,
    sendMessage,
    createDirectChat,
    markAsRead,
    setTyping,
    fetchMessages,
    refetchRooms: fetchRooms,
  };
};
